import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import axios from 'axios';
import { API_URL } from '../../consts';

const ORGANIZATION_CATEGORIES = [
  'Technology',
  'Healthcare',
  'Finance',
  'Education',
  'Retail',
  'Manufacturing',
  'Consulting',
  'Marketing',
  'Real Estate',
  'Hospitality',
  'Other'
];

const OrganizationOnboardingDialog = ({ open, onClose, onComplete }) => {
  const [step, setStep] = useState(1); // 1: Purpose selection, 2: Organization details
  const [purpose, setPurpose] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Organization form fields
  const [orgData, setOrgData] = useState({
    name: '',
    category: '',
    address: {
      street: '',
      city: '',
      country: '',
      zipcode: ''
    }
  });

  const handlePurposeSelection = (value) => {
    setPurpose(value);
  };

  const handlePrivateSelection = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.put(`${API_URL}/profile`, {
        part_of_organization: false
      }, {
        withCredentials: true
      });
      
      onComplete({ part_of_organization: false });
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.response?.data?.error || 'Failed to update profile. Please try again.');
      setLoading(false);
    }
  };

  const handleOrgFormChange = (field, value) => {
    if (field.startsWith('address.')) {
      const addressField = field.split('.')[1];
      setOrgData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setOrgData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleOrganizationSubmit = async () => {
    // Validation
    if (!orgData.name.trim()) {
      setError('Organization name is required');
      return;
    }
    if (!orgData.category) {
      setError('Please select a category');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_URL}/tenants/organizations`, {
        name: orgData.name,
        category: orgData.category,
        address: orgData.address
      }, {
        withCredentials: true
      });
      
      if (response.data && response.data.tenant) {
        onComplete({
          part_of_organization: true,
          organization_id: response.data.tenant.id,
          organization: response.data.tenant
        });
      }
    } catch (err) {
      console.error('Error creating organization:', err);
      setError(err.response?.data?.error || 'Failed to create organization. Please try again.');
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <Box sx={{ py: 3 }}>
      <Typography variant="h6" sx={{ mb: 3, textAlign: 'center', fontWeight: 600 }}>
        For what purpose are you using the system?
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      <RadioGroup
        value={purpose}
        onChange={(e) => handlePurposeSelection(e.target.value)}
      >
        <Box
          sx={{
            mb: 2,
            p: 3,
            border: '2px solid',
            borderColor: purpose === 'private' ? 'primary.main' : 'grey.300',
            borderRadius: 2,
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'rgba(99, 102, 241, 0.05)'
            }
          }}
          onClick={() => handlePurposeSelection('private')}
        >
          <FormControlLabel
            value="private"
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PersonIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Private
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    For personal use only
                  </Typography>
                </Box>
              </Box>
            }
          />
        </Box>

        <Box
          sx={{
            p: 3,
            border: '2px solid',
            borderColor: purpose === 'organizational' ? 'primary.main' : 'grey.300',
            borderRadius: 2,
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'rgba(99, 102, 241, 0.05)'
            }
          }}
          onClick={() => handlePurposeSelection('organizational')}
        >
          <FormControlLabel
            value="organizational"
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <BusinessIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Organizational
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    For business or team collaboration
                  </Typography>
                </Box>
              </Box>
            }
          />
        </Box>
      </RadioGroup>
    </Box>
  );

  const renderStep2 = () => (
    <Box sx={{ py: 2 }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        Organization Details
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Organization Name *"
        value={orgData.name}
        onChange={(e) => handleOrgFormChange('name', e.target.value)}
        sx={{ mb: 2 }}
        required
      />

      <FormControl fullWidth sx={{ mb: 2 }} required>
        <InputLabel>Category *</InputLabel>
        <Select
          value={orgData.category}
          onChange={(e) => handleOrgFormChange('category', e.target.value)}
          label="Category *"
        >
          {ORGANIZATION_CATEGORIES.map((cat) => (
            <MenuItem key={cat} value={cat}>
              {cat}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography variant="subtitle2" sx={{ mb: 1, mt: 3, fontWeight: 600 }}>
        Address (Optional)
      </Typography>

      <TextField
        fullWidth
        label="Street"
        value={orgData.address.street}
        onChange={(e) => handleOrgFormChange('address.street', e.target.value)}
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          fullWidth
          label="City"
          value={orgData.address.city}
          onChange={(e) => handleOrgFormChange('address.city', e.target.value)}
        />
        <TextField
          fullWidth
          label="Country"
          value={orgData.address.country}
          onChange={(e) => handleOrgFormChange('address.country', e.target.value)}
        />
      </Box>

      <TextField
        fullWidth
        label="Zip Code"
        value={orgData.address.zipcode}
        onChange={(e) => handleOrgFormChange('address.zipcode', e.target.value)}
        sx={{ mb: 2 }}
      />
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing by clicking outside or pressing escape
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        onClose();
      }}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon color="primary" />
          <Typography variant="h6" component="span">
            Welcome to Datarate
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {step === 1 ? renderStep1() : renderStep2()}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        {step === 1 ? (
          <Button
            variant="contained"
            onClick={() => {
              if (purpose === 'private') {
                handlePrivateSelection();
              } else if (purpose === 'organizational') {
                setStep(2);
              }
            }}
            disabled={!purpose || loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Saving...' : 'Continue'}
          </Button>
        ) : (
          <>
            <Button
              onClick={() => {
                setStep(1);
                setPurpose('');
                setError('');
              }}
              disabled={loading}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleOrganizationSubmit}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Creating...' : 'Create Organization'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OrganizationOnboardingDialog;
